import type { AppRouter } from "@repo/vihangam-yog-backend/router";
import { eventCatalog, findEventEntry } from "@repo/vihangam-yog-shared";
import type { inferRouterOutputs } from "@trpc/server";
import {
  ArrowUp,
  Check,
  Copy,
  Download,
  Eye,
  EyeOff,
  Lock,
  MessageSquareText,
  RotateCcw,
} from "lucide-react";
import { useState } from "react";

import { CHAPTER } from "../chapter";
import { StatPlate } from "../components/DataKit";
import { friendlyError } from "../lib/friendlyError";
import { trpc } from "../trpc";

import { formatTime } from "./event-registrations/shared";
import { brandedQrPng } from "./panel-questions/brandedQr";

type Filter = "queue" | "answered" | "hidden";
type Sort = "votes" | "newest";

const FILTERS: { id: Filter; label: string }[] = [
  { id: "queue", label: "Queue" },
  { id: "answered", label: "Answered" },
  { id: "hidden", label: "Hidden" },
];
const SORTS: { id: Sort; label: string }[] = [
  { id: "votes", label: "Most voted" },
  { id: "newest", label: "Newest" },
];

const primaryButton =
  "inline-flex min-h-11 items-center gap-1.5 rounded-full bg-[color:var(--color-ink)] px-4 text-[14px] font-semibold text-[color:var(--color-paper)] transition-opacity hover:opacity-90 disabled:opacity-50";
const outlineButton =
  "inline-flex min-h-11 items-center gap-1.5 rounded-full border border-[color:var(--color-line-strong)] px-3.5 text-[14px] font-semibold text-[color:var(--color-ink)] transition-colors hover:border-[color:var(--color-ink)] hover:bg-[color:var(--color-paper)] disabled:opacity-50 disabled:hover:border-[color:var(--color-line-strong)] disabled:hover:bg-transparent";

const panelEvents = eventCatalog.filter(
  (event) => event.collectsPanelQuestions,
);

/**
 * The moderator's desk for a panel night. Questions arrive from three
 * places: the registration form and the anonymous ask page before the
 * event (private, never shown to the room), and the live board on the
 * night (public, with votes). This page opens and closes the floor,
 * puts one question up for the panel at a time, and marks it answered.
 */
export function PanelQuestions() {
  const [eventSlug, setEventSlug] = useState(panelEvents[0]?.slug ?? "");
  const event = panelEvents.find((entry) => entry.slug === eventSlug);

  return (
    <section className="px-5 py-8 md:px-10 md:py-10">
      <header className="flex flex-wrap items-end justify-between gap-4 border-b border-[color:var(--color-line)] pb-6">
        <div>
          <h1 className="page-title">Panel questions</h1>
          {event && (
            <p className="mt-2 text-[15px] text-[color:var(--color-ink-soft)]">
              {event.title} · {event.dateLabel}
            </p>
          )}
        </div>
        {panelEvents.length > 1 && (
          <label className="flex items-center gap-3 text-[15px] font-semibold text-[color:var(--color-ink)]">
            Event
            <select
              value={eventSlug}
              onChange={(e) => setEventSlug(e.target.value)}
              className="min-h-11 max-w-full rounded-md border border-[color:var(--color-line-strong)] bg-[color:var(--color-paper)] px-3 text-[16px] font-normal"
            >
              {panelEvents.map((entry) => (
                <option key={entry.slug} value={entry.slug}>
                  {entry.title}
                </option>
              ))}
            </select>
          </label>
        )}
      </header>
      {event ? (
        <QuestionsPanel key={eventSlug} eventSlug={eventSlug} />
      ) : (
        <p className="empty-state">No events are collecting panel questions.</p>
      )}
    </section>
  );
}

function QuestionsPanel({ eventSlug }: { eventSlug: string }) {
  const [filter, setFilter] = useState<Filter>("queue");
  const [sort, setSort] = useState<Sort>("votes");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [shareMessage, setShareMessage] = useState<string | null>(null);
  const utils = trpc.useUtils();
  const query = trpc.questions.queue.useQuery(
    { eventSlug },
    {
      refetchInterval: 2_000,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: true,
      refetchOnReconnect: "always",
      retry: 2,
    },
  );
  const refresh = () => utils.questions.queue.invalidate({ eventSlug });
  const session = trpc.questions.setSession.useMutation({ onSuccess: refresh });
  const answer = trpc.questions.setAnswered.useMutation({ onSuccess: refresh });
  const hide = trpc.questions.setHidden.useMutation({ onSuccess: refresh });
  const canGoLive = findEventEntry(eventSlug)?.liveQuestionsEnabled === true;
  const publicUrl = `${CHAPTER.siteUrl}/events/${eventSlug}/live`;
  const connectionPaused = query.isError || query.fetchStatus === "paused";
  const state = query.data?.state ?? "not_open";
  const isOpen = state === "open";
  const rows = query.data?.questions ?? [];
  const selected = rows.find((q) => q.id === selectedId);
  const byOrder = (a: Question, b: Question) => {
    if (sort === "votes" && a.votes !== b.votes) {
      return b.votes - a.votes;
    }
    return b.createdAt.localeCompare(a.createdAt) || a.id.localeCompare(b.id);
  };
  const queue = rows
    .filter((q) => q.hiddenAt === null && q.answeredAt === null)
    .sort(byOrder);
  const answered = rows.filter(
    (q) => q.hiddenAt === null && q.answeredAt !== null,
  );
  const hidden = rows.filter((q) => q.hiddenAt !== null);
  const visibleRows =
    filter === "queue"
      ? queue
      : (filter === "answered" ? answered : hidden).sort(byOrder);
  const counts = {
    queue: queue.length,
    answered: answered.length,
    hidden: hidden.length,
  } satisfies Record<Filter, number>;
  const votes = rows
    .filter((q) => q.hiddenAt === null)
    .reduce((sum, q) => sum + q.votes, 0);
  const mutationError = session.error ?? answer.error ?? hide.error;

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setShareMessage("Attendee link copied.");
    } catch {
      setShareMessage("Couldn't copy automatically. Select and copy the link.");
    }
  }
  async function downloadQr() {
    try {
      const url = URL.createObjectURL(await brandedQrPng(publicUrl));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${eventSlug}-live-questions.png`;
      // On the page for the click, and the URL kept a moment: iOS Safari
      // ignores a detached link and can cancel a download revoked at once.
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      setShareMessage("QR code downloaded. It points to the public website.");
    } catch {
      setShareMessage("Couldn't generate the QR code. Please try again.");
    }
  }
  function markAnswered(q: Question) {
    answer.mutate({ eventSlug, id: q.id, answered: q.answeredAt === null });
  }
  function answeredAndNext(q: Question) {
    const next = queue.find((candidate) => candidate.id !== q.id);
    answer.mutate({ eventSlug, id: q.id, answered: true });
    setSelectedId(next?.id ?? null);
  }
  function toggleHidden(q: Question) {
    hide.mutate({ eventSlug, id: q.id, hidden: q.hiddenAt === null });
  }

  return (
    <section
      className="mt-7 text-[color:var(--color-ink)]"
      aria-label="Panel questions"
    >
      <p className="max-w-2xl text-[16px] leading-relaxed text-[color:var(--color-ink-soft)]">
        Questions sent before the event stay private to this page. Questions
        from the live board are public the moment they arrive, so hide anything
        that should come off it.
      </p>

      {canGoLive && query.data && (
        <div className="surface mt-6 flex flex-wrap items-center justify-between gap-4 p-5">
          <div className="flex items-start gap-3">
            <span
              aria-hidden
              className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${
                isOpen
                  ? "bg-[color:var(--color-amber)]"
                  : "border border-[color:var(--color-line-strong)]"
              }`}
            />
            <div>
              <p className="text-[18px] leading-tight font-semibold">
                {isOpen
                  ? "The floor is open"
                  : state === "closed"
                    ? "The floor is closed"
                    : "The floor hasn't opened yet"}
              </p>
              <p className="mt-1 text-[15px] text-[color:var(--color-ink-soft)]">
                {isOpen
                  ? "Attendees can send questions and vote."
                  : state === "closed"
                    ? "Attendees can read the board but no longer send or vote."
                    : "Attendees can read the board and draft. Open it when the panel is ready."}
              </p>
            </div>
          </div>
          <button
            type="button"
            disabled={session.isPending}
            onClick={() =>
              session.mutate({ eventSlug, state: isOpen ? "closed" : "open" })
            }
            className={isOpen ? outlineButton : primaryButton}
          >
            {session.isPending
              ? "Saving…"
              : isOpen
                ? "Close the floor"
                : "Open the floor"}
          </button>
        </div>
      )}

      {canGoLive && (
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 px-1 text-[14px]">
          <span className="eyebrow">Attendee link</span>
          <a
            href={publicUrl}
            target="_blank"
            rel="noreferrer"
            className="break-all underline underline-offset-4"
          >
            {publicUrl.replace(/^https:\/\//, "")}
          </a>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void copyLink()}
              className={outlineButton}
            >
              <Copy size={16} aria-hidden="true" />
              Copy link
            </button>
            <button
              type="button"
              onClick={() => void downloadQr()}
              className={outlineButton}
            >
              <Download size={16} aria-hidden="true" />
              Download QR code
            </button>
          </div>
          {shareMessage && (
            <p role="status" className="text-[color:var(--color-ink-soft)]">
              {shareMessage}
            </p>
          )}
        </div>
      )}

      {query.isPending && (
        <p className="mt-8 text-[color:var(--color-ink-soft)]" role="status">
          Loading questions…
        </p>
      )}
      {connectionPaused && (
        <div
          role="alert"
          className="mt-5 rounded-lg border border-red-700/30 p-4 text-[15px]"
        >
          <p>
            {query.data
              ? "Updates have paused. The questions already here are still good to work from."
              : "Couldn't load the question queue."}
          </p>
          <button
            type="button"
            onClick={() => void query.refetch()}
            className={`${outlineButton} mt-3`}
          >
            Try again
          </button>
        </div>
      )}
      {mutationError && (
        <p role="alert" className="mt-5 text-[15px] text-red-700">
          {friendlyError(mutationError)}
        </p>
      )}

      {query.data && (
        <>
          <div className="mt-8 flex flex-wrap items-end gap-4">
            <StatPlate value={counts.queue} label="In queue" tone="amber" />
            <StatPlate value={counts.answered} label="Answered" />
            {canGoLive && <StatPlate value={votes} label="Votes" />}
            <p
              role="status"
              className="ml-auto flex items-center gap-2 pb-1 text-[14px] text-[color:var(--color-ink-soft)] tabular-nums"
            >
              <span
                aria-hidden
                className={`h-2 w-2 rounded-full ${
                  connectionPaused
                    ? "bg-[color:var(--color-ink-soft)]"
                    : "bg-[color:var(--color-amber)]"
                }`}
              />
              {connectionPaused
                ? "Paused"
                : `Live · ${formatTime(new Date(query.dataUpdatedAt).toISOString())}`}
            </p>
          </div>

          {selected && (
            <div
              role="region"
              aria-label="Asking now"
              className="mt-8 border-2 border-[color:var(--color-amber)]/50 bg-[color:var(--color-amber)]/8 p-5 md:p-7"
            >
              <div className="flex items-center justify-between gap-3">
                <p className="eyebrow text-[color:var(--color-amber)]">
                  Asking now
                </p>
                <button
                  type="button"
                  onClick={() => setSelectedId(null)}
                  className="min-h-11 px-2 text-[14px] font-medium text-[color:var(--color-ink-soft)] underline-offset-4 hover:text-[color:var(--color-ink)] hover:underline"
                >
                  Clear
                </button>
              </div>
              <p className="mt-3 text-[clamp(1.5rem,3vw,2rem)] leading-[1.35] font-medium whitespace-pre-wrap break-words">
                {selected.question}
              </p>
              <p className="mt-4 text-[15px] text-[color:var(--color-ink-soft)]">
                {describe(selected)}
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {selected.answeredAt === null ? (
                  <button
                    type="button"
                    disabled={answer.isPending}
                    onClick={() => answeredAndNext(selected)}
                    className={primaryButton}
                  >
                    <Check size={16} aria-hidden="true" />
                    {queue.some((q) => q.id !== selected.id)
                      ? "Answered · next up"
                      : "Mark answered"}
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={answer.isPending}
                    onClick={() => markAnswered(selected)}
                    className={outlineButton}
                  >
                    <RotateCcw size={16} aria-hidden="true" />
                    Reopen
                  </button>
                )}
                <button
                  type="button"
                  disabled={hide.isPending}
                  onClick={() => toggleHidden(selected)}
                  className={outlineButton}
                >
                  {selected.hiddenAt ? (
                    <Eye size={16} aria-hidden="true" />
                  ) : (
                    <EyeOff size={16} aria-hidden="true" />
                  )}
                  {selected.hiddenAt ? "Restore" : "Hide"}
                </button>
              </div>
            </div>
          )}

          <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-1.5">
              {FILTERS.map((f) => {
                const isActive = filter === f.id;
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setFilter(f.id)}
                    aria-pressed={isActive}
                    className={`min-h-11 rounded-full px-3.5 text-[14px] font-semibold transition-colors ${
                      isActive
                        ? "bg-[color:var(--color-amber)] text-[color:var(--color-paper)]"
                        : "border border-[color:var(--color-line-strong)] text-[color:var(--color-ink-soft)] hover:border-[color:var(--color-ink)] hover:text-[color:var(--color-ink)]"
                    }`}
                  >
                    {f.label}
                    <span
                      className={`ml-1.5 tabular-nums ${
                        isActive
                          ? "opacity-80"
                          : "text-[color:var(--color-ink-faint)]"
                      }`}
                    >
                      {counts[f.id]}
                    </span>
                  </button>
                );
              })}
            </div>
            <div
              className="flex items-center gap-4 text-[14px]"
              role="group"
              aria-label="Sort"
            >
              <span
                aria-hidden="true"
                className="text-[color:var(--color-ink-soft)]"
              >
                Sort by
              </span>
              {SORTS.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setSort(s.id)}
                  aria-pressed={sort === s.id}
                  className={`min-h-11 underline-offset-4 transition-colors ${
                    sort === s.id
                      ? "font-semibold text-[color:var(--color-ink)] underline"
                      : "text-[color:var(--color-ink-soft)] hover:text-[color:var(--color-ink)]"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {visibleRows.length === 0 && (
            <p className="empty-state">
              {filter === "queue"
                ? rows.length === 0
                  ? "No questions yet."
                  : "The queue is clear."
                : filter === "answered"
                  ? "Nothing answered yet."
                  : "Nothing hidden."}
            </p>
          )}

          <ul className="mt-4 space-y-3">
            {visibleRows.map((q) => {
              const asking = q.id === selectedId;
              return (
                <li
                  key={q.id}
                  className={`surface p-4 md:p-5 ${
                    asking ? "border-[color:var(--color-amber)]/60" : ""
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex w-14 shrink-0 flex-col items-center pt-0.5 text-[color:var(--color-ink-soft)]">
                      {q.visibility === "public" ? (
                        <>
                          <ArrowUp size={16} aria-hidden="true" />
                          <span className="mt-0.5 text-[20px] leading-none font-semibold text-[color:var(--color-ink)] tabular-nums">
                            {q.votes}
                          </span>
                          <span className="sr-only">
                            {q.votes === 1 ? "vote" : "votes"}
                          </span>
                        </>
                      ) : (
                        <>
                          <Lock size={16} aria-hidden="true" className="mt-1" />
                          <span className="mt-0.5 text-[13px] font-semibold">
                            Private
                          </span>
                        </>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[17px] leading-[1.5] whitespace-pre-wrap break-words">
                        {q.question}
                      </p>
                      <p className="mt-1.5 text-[14px] text-[color:var(--color-ink-soft)]">
                        {describe(q)}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          disabled={asking}
                          onClick={() => setSelectedId(q.id)}
                          aria-label={`${asking ? "Asking now" : "Ask this"}: ${q.question}`}
                          className={outlineButton}
                        >
                          <MessageSquareText size={16} aria-hidden="true" />
                          {asking ? "Asking now" : "Ask this"}
                        </button>
                        <button
                          type="button"
                          disabled={answer.isPending}
                          onClick={() => markAnswered(q)}
                          className={outlineButton}
                        >
                          {q.answeredAt ? (
                            <RotateCcw size={16} aria-hidden="true" />
                          ) : (
                            <Check size={16} aria-hidden="true" />
                          )}
                          {q.answeredAt ? "Reopen" : "Answered"}
                        </button>
                        <button
                          type="button"
                          disabled={hide.isPending}
                          onClick={() => toggleHidden(q)}
                          className={outlineButton}
                        >
                          {q.hiddenAt ? (
                            <Eye size={16} aria-hidden="true" />
                          ) : (
                            <EyeOff size={16} aria-hidden="true" />
                          )}
                          {q.hiddenAt ? "Restore" : "Hide"}
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}

type Question =
  inferRouterOutputs<AppRouter>["questions"]["queue"]["questions"][number];

/** Who asked, when, and where it can be seen, on one line. */
function describe(q: Question): string {
  const parts = [q.displayName ?? "Anonymous"];
  parts.push(
    q.source === "live"
      ? `asked ${formatTime(q.createdAt)}`
      : "sent before the event",
  );
  parts.push(
    q.visibility === "private"
      ? "private"
      : `${q.votes} ${q.votes === 1 ? "vote" : "votes"}`,
  );
  if (q.answeredAt) {
    parts.push("answered");
  }
  if (q.hiddenAt) {
    parts.push("hidden");
  }
  return parts.join(" · ");
}
